/**
 * SharePreviewPublic — public surface for `/share/$token`.
 *
 * Renders the safe-share card a recipient can open WITHOUT authenticating
 * (the route is allow-listed as public). Three states, all humane (NEVER
 * a generic "something went wrong"):
 *
 *   - `valid`   → the share card (kicker / title / subtitle / badges) plus
 *                 a gentle CTA to discover & install Cuidari.
 *   - `expired` → "este link expirou" + CTA to discover.
 *   - `invalid` → "este link não é válido" + CTA.
 *
 * The card body is built by `buildSafeSharePreview` from `cuidari-core`,
 * the same builder the native app uses, so the web preview matches what
 * the sharer saw.
 *
 * Privacy: the decoder (`sharePayload.ts`) only ever exposes counts and
 * labels — never amounts, vendor names, emails, or user ids. See the
 * privacy guard test in `__tests__/sharePayload.test.ts`.
 *
 * All copy lives under the single `shareV4` i18n key block.
 */
import type { CSSProperties } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { buildSafeSharePreview } from '@francisquiniai/cuidari-core/share'

import { decodeShareToken, type SharePayload } from './sharePayload'

interface SharePreviewPublicProps {
  token: string | null | undefined
  /** Override for tests; defaults to the system clock. */
  now?: Date
}

const page: CSSProperties = {
  minHeight: '100dvh',
  // Warm teal-tinted backdrop — same brand DNA as /login, /lista and the
  // onboarding gate, so a recipient who lands here unauthenticated sees a
  // calm, on-brand surface (not a cold gray void around a floating card).
  background:
    'radial-gradient(1000px 480px at 50% -8%, rgba(13, 148, 136, 0.16), transparent 60%), linear-gradient(180deg, #f3f4f6 0%, #f8fafc 45%, #f1f5f9 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(20px, 6vw, 56px) 16px',
  boxSizing: 'border-box',
}

const shell: CSSProperties = {
  width: '100%',
  maxWidth: 440,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}

const brandRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  color: 'var(--color-primary, #0d9488)',
  fontWeight: 800,
  fontSize: 15,
  letterSpacing: '-0.01em',
}

const brandDot: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 999,
  background: 'var(--color-primary, #0d9488)',
  display: 'inline-block',
}

const card: CSSProperties = {
  width: '100%',
  background: 'var(--color-surface, #ffffff)',
  borderRadius: 28,
  padding: 'clamp(22px, 5vw, 30px)',
  boxShadow: '0 28px 68px rgba(15, 23, 42, 0.16)',
  border: '1px solid var(--color-border, #e2e8f0)',
  color: 'var(--color-fg, #0f172a)',
  boxSizing: 'border-box',
  overflow: 'hidden',
}

const artifactBand: CSSProperties = {
  borderRadius: 24,
  background: 'linear-gradient(135deg, #0f766e, #172554)',
  color: 'white',
  padding: '20px 18px',
  margin: '0 0 22px',
}

const kicker: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--color-primary, #0d9488)',
  marginBottom: 12,
  display: 'block',
}

const title: CSSProperties = {
  fontSize: 'clamp(24px, 6vw, 30px)',
  fontWeight: 900,
  lineHeight: 1.12,
  margin: '0 0 10px',
}

const subtitle: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.55,
  color: 'var(--color-muted, #475569)',
  margin: '0 0 20px',
}

const badgesRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 22,
}

const badge: CSSProperties = {
  background: '#ecfdf5',
  color: 'var(--color-primary, #0d9488)',
  borderRadius: 999,
  padding: '7px 13px',
  fontSize: 12,
  fontWeight: 800,
}

const ctaRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const primaryBtn: CSSProperties = {
  background: 'var(--color-primary, #0d9488)',
  color: 'white',
  padding: '15px 22px',
  borderRadius: 999,
  fontSize: 15,
  fontWeight: 700,
  textDecoration: 'none',
  textAlign: 'center',
  border: 'none',
}

const ghostBtn: CSSProperties = {
  background: 'transparent',
  color: 'var(--color-fg, #0f172a)',
  padding: '13px 22px',
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 600,
  textDecoration: 'none',
  textAlign: 'center',
  border: '1.5px solid var(--color-border, #e2e8f0)',
}

const privacyNote: CSSProperties = {
  margin: '16px 0 0',
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--color-muted, #64748b)',
  textAlign: 'center',
}

const footerNote: CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: 'var(--color-muted, #94a3b8)',
  textAlign: 'center',
}

function Brand() {
  const { t } = useTranslation('common')
  return (
    <div style={brandRow}>
      <span aria-hidden style={brandDot} />
      <span>{t('shareV4.brand')}</span>
    </div>
  )
}

function ValidShareCard({ payload }: { payload: SharePayload }) {
  const { t } = useTranslation('common')
  const preview = useMemo(
    () =>
      buildSafeSharePreview({
        variant: payload.variant,
        monthLabel: payload.monthLabel ?? null,
        billsCount: payload.billsCount ?? null,
        paidCount: payload.paidCount ?? null,
        milestoneLabel: payload.milestoneLabel ?? null,
      }),
    [payload],
  )

  return (
    <article data-testid="share-preview-valid" style={card}>
      <div style={artifactBand}>
        <span style={{ ...kicker, color: 'rgba(255,255,255,0.72)', marginBottom: 10 }}>
          Artefato compartilhável
        </span>
        <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.12, fontWeight: 900 }}>
          Cuidado que dá para mostrar sem abrir sua vida financeira
        </h2>
        <p style={{ margin: '12px 0 0', color: 'rgba(255,255,255,0.76)', fontSize: 14, lineHeight: 1.5 }}>
          O cartão transforma progresso em marco público, mantendo valores e contas fora da conversa.
        </p>
      </div>
      <span style={kicker}>{t('shareV4.kicker')}</span>
      <h1 style={title}>{preview.title}</h1>
      <p style={subtitle}>{preview.subtitle}</p>
      {preview.badges.length > 0 && (
        <div data-testid="share-preview-badges" style={badgesRow}>
          {preview.badges.map((b) => (
            <span key={b.id} style={badge}>
              {b.label}
            </span>
          ))}
        </div>
      )}
      <div style={ctaRow}>
        <a data-testid="share-preview-cta" href="/" style={primaryBtn}>
          {t('shareV4.cta')}
        </a>
        <a
          data-testid="share-preview-cta-secondary"
          href="/instalar"
          style={ghostBtn}
        >
          {t('shareV4.ctaSecondary')}
        </a>
      </div>
      <p style={privacyNote}>{t('shareV4.privacyNote')}</p>
    </article>
  )
}

function HumaneCard({
  testId,
  titleText,
  bodyText,
}: {
  testId: string
  titleText: string
  bodyText: string
}) {
  const { t } = useTranslation('common')
  return (
    <article data-testid={testId} style={card}>
      <span style={kicker}>{t('shareV4.kicker')}</span>
      <h1 style={title}>{titleText}</h1>
      <p style={subtitle}>{bodyText}</p>
      <div style={ctaRow}>
        <a data-testid="share-preview-cta" href="/" style={primaryBtn}>
          {t('shareV4.discoverCta')}
        </a>
      </div>
    </article>
  )
}

export function SharePreviewPublic({ token, now }: SharePreviewPublicProps) {
  const { t } = useTranslation('common')
  const result = useMemo(() => decodeShareToken(token, now), [token, now])

  return (
    <main data-testid="share-preview-public" style={page}>
      <div style={shell}>
        <Brand />
        {result.kind === 'valid' ? (
          <ValidShareCard payload={result.payload} />
        ) : result.kind === 'expired' ? (
          <HumaneCard
            testId="share-preview-expired"
            titleText={t('shareV4.expiredTitle')}
            bodyText={t('shareV4.expiredBody')}
          />
        ) : (
          <HumaneCard
            testId="share-preview-invalid"
            titleText={t('shareV4.invalidTitle')}
            bodyText={t('shareV4.invalidBody')}
          />
        )}
        <p style={footerNote}>{t('shareV4.footer')}</p>
      </div>
    </main>
  )
}
